import { useId, useRef, useState } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { Search } from "lucide-react";
import { quietControlClass } from "./QuietField";

// 邊打邊搜尋的欄位(業主 2026-11-02,客戶反映「客戶名稱有時能打字找、有時不行,打錯還改不掉」)。
//
// 原本是瀏覽器原生的下拉選單:它的「打字」只是跳到開頭字相同的那一項,停一下就重來,
// 也不能退格。這裡改成一般的輸入框:打字 = 即時篩選清單,可以退格、可以改字;
// 上下鍵移動、Enter 選、Esc 收起來;滑鼠點也可以。
//
// 兩種用法:
// * **挑選型**(客戶):不給 `onTextChange`。打的字只用來找,沒選就離開會回到原本的值。
// * **自由型**(聯絡人):給 `onTextChange`。打的字就是欄位的值,清單只是建議。
//
// 樣子與 QuietField 同一套(標籤在左、欄位在右),但多一條常駐底線與放大鏡,看得出來可以打字。

export interface PickerOption {
  key: string;
  label: string;
  /** 清單裡第二行的小字(統編、職稱、電話…)。 */
  hint?: string;
  /** 拿來比對的字;沒給就用 label。 */
  match?: string;
}

export interface PickerAction {
  key: string;
  label: ReactNode;
  onSelect: () => void;
}

export interface SearchPickerProps {
  label: string;
  /** 平常(沒在打字時)顯示的字。 */
  text: string;
  options: PickerOption[];
  onPick: (option: PickerOption) => void;
  /** 自由型:打的字直接是值。 */
  onTextChange?: (text: string) => void;
  /** 清單最下面的動作(例如「新增客戶」),拿目前打的字決定要不要出現。 */
  actions?: (query: string) => PickerAction[];
  placeholder?: string;
  readOnly?: boolean;
  /** 最多列幾筆;其餘的請使用者再多打幾個字。 */
  limit?: number;
  /** 沒打字時要不要也列出清單(聯絡人:點進去就看得到這家的所有窗口)。 */
  showAllWhenEmpty?: boolean;
  /** 只有一個網頁欄位,不畫左邊的標籤(新增報價單頁用自己的標籤)。 */
  layout?: "row" | "stacked";
}

export function normalize(text: string): string {
  return text.trim().toLowerCase().replace(/\s+/g, "");
}

export function SearchPicker({
  label, text, options, onPick, onTextChange, actions, placeholder, readOnly = false, limit = 30,
  showAllWhenEmpty = false, layout = "row",
}: SearchPickerProps) {
  const id = useId();
  const listId = `${id}-list`;
  const free = onTextChange !== undefined;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState<string | null>(null); // 挑選型:null = 沒在打字
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const shown = free ? text : query ?? text;
  const needle = normalize(free ? text : query ?? "");
  const matched = needle === ""
    ? (showAllWhenEmpty || !free ? options : [])
    : options.filter((o) => normalize(o.match ?? o.label).includes(needle));
  const visible = matched.slice(0, limit);
  const extra = actions ? actions((free ? text : query ?? "").trim()) : [];
  const rows = visible.length + extra.length;
  const expanded = open && !readOnly && rows > 0;

  const close = () => {
    setOpen(false);
    setQuery(null);
    setActive(0);
  };

  const choose = (index: number) => {
    if (index < visible.length) onPick(visible[index]);
    else extra[index - visible.length]?.onSelect();
    close();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActive((i) => (rows === 0 ? 0 : (i + 1) % rows));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      setActive((i) => (rows === 0 ? 0 : (i - 1 + rows) % rows));
    } else if (event.key === "Enter" && expanded) {
      event.preventDefault();
      choose(Math.min(active, rows - 1));
    } else if (event.key === "Escape" && open) {
      event.preventDefault();
      close();
    }
  };

  const input = (
    <div className="relative min-w-0 flex-1">
      <input
        ref={inputRef}
        id={id}
        role="combobox"
        aria-expanded={expanded}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={expanded ? `${id}-opt-${active}` : undefined}
        autoComplete="off"
        value={shown}
        placeholder={placeholder}
        readOnly={readOnly || undefined}
        className={
          quietControlClass({ readOnly }) +
          (readOnly ? "" : "border-b-[color:var(--field-hover)] ") + "pr-7"
        }
        onFocus={(event) => {
          if (readOnly) return;
          setOpen(true);
          // 挑選型:點進來就全選,直接打字就是重新找,不會接在原本的名字後面。
          if (!free) event.target.select();
        }}
        onBlur={close}
        onChange={(event) => {
          setOpen(true);
          setActive(0);
          if (free) onTextChange?.(event.target.value);
          else setQuery(event.target.value);
        }}
        onKeyDown={onKeyDown}
      />
      {readOnly ? null : (
        <Search aria-hidden="true" size={14}
          className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-3)]" />
      )}
      {expanded ? (
        <ul
          id={listId}
          role="listbox"
          aria-label={`${label}清單`}
          className="absolute left-0 right-0 top-full z-30 m-0 mt-1 max-h-72 list-none overflow-y-auto rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface)] p-1 shadow-lg"
        >
          {visible.map((option, index) => (
            <li
              key={option.key}
              id={`${id}-opt-${index}`}
              role="option"
              aria-selected={index === active}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(index)}
              onMouseEnter={() => setActive(index)}
              className={"cursor-pointer rounded px-2 py-1 text-sm " +
                (index === active ? "bg-[var(--surface-2)] text-[var(--text)]" : "text-[var(--text)]")}
            >
              <div>{option.label}</div>
              {option.hint ? <div className="text-xs text-[var(--text-3)]">{option.hint}</div> : null}
            </li>
          ))}
          {matched.length > visible.length ? (
            <li role="presentation" className="px-2 py-1 text-xs text-[var(--text-3)]">
              還有 {matched.length - visible.length} 筆,再多打幾個字
            </li>
          ) : null}
          {extra.map((action, i) => {
            const index = visible.length + i;
            return (
              <li
                key={action.key}
                id={`${id}-opt-${index}`}
                role="option"
                aria-selected={index === active}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => choose(index)}
                onMouseEnter={() => setActive(index)}
                className={"cursor-pointer rounded border-t border-[var(--border)] px-2 py-1 text-sm text-[var(--accent)] " +
                  (index === active ? "bg-[var(--surface-2)]" : "")}
              >
                {action.label}
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );

  if (layout === "stacked") {
    return (
      <div className="flex flex-col gap-1">
        <label htmlFor={id} className="text-sm font-medium text-[var(--text)]">{label}</label>
        {input}
      </div>
    );
  }
  return (
    <div className="flex min-w-0 items-start gap-2 py-0.5">
      <label htmlFor={id}
        className="mt-1 w-[6.5rem] shrink-0 whitespace-nowrap text-[13px] leading-5 text-[var(--text-3)]">
        {label}
      </label>
      {input}
    </div>
  );
}
